import { useState, useEffect, useCallback } from "react";
import { DataGridPro as DataGrid } from "@mui/x-data-grid-pro";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import CategoryIcon from "@mui/icons-material/Category";
import { Box, CircularProgress } from "@mui/material";
import { useSnackbar } from "notistack";
import { useApi } from "../../utils/Api";
import Translate from "../../utils/Translate";
import DotMenu from "../../components/DotMenu";
import SpeedMenu from "../../components/SpeedMenu";
import HelpArticleEditDialog from "../../components/admin/help-pages/HelpArticleEditDialog";
import HelpCategoriesDialog from "../../components/admin/help-pages/HelpCategoriesDialog";
import Preloader from "../../components/Preloader";

const MAIN_URL = import.meta.env.VITE_MAIN_URL;

export default function HelpArticlesPage() {
  const api = useApi();
  const { enqueueSnackbar } = useSnackbar();

  const [pages, setPages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const reloadList = useCallback(async () => {
    setLoadingList(true);
    const response = await api.fetch(`${MAIN_URL}help/admin/pages`, false, "GET");
    if (response && response.isSuccessful && Array.isArray(response.helpPages)) {
      setPages(response.helpPages);
    }
    setLoadingList(false);
  }, [api]);

  const loadCategories = useCallback(async () => {
    const response = await api.fetch(`${MAIN_URL}help/admin/categories`, false, "GET");
    if (response && response.isSuccessful && Array.isArray(response.categories)) {
      setCategories(response.categories);
    }
  }, [api]);

  useEffect(() => {
    reloadList();
    loadCategories();
  }, [reloadList, loadCategories]);

  const handleDelete = useCallback(
    async (row) => {
      const label = row.titleSwe || row.titleEng || row.titleFra || row.helpPageId;
      if (!window.confirm(`${Translate.get("HelpArticleDeleteConfirm")} '${label}' (${row.helpPageId})?`)) {
        return;
      }
      const response = await api.fetch(
        `${MAIN_URL}help/admin/pages/${encodeURIComponent(row.helpPageId)}`,
        true,
        "DELETE",
      );
      if (response && response.isSuccessful) {
        enqueueSnackbar(Translate.get("HelpArticleDeleted"), { variant: "success" });
        reloadList();
      }
    },
    [api, enqueueSnackbar, reloadList],
  );

  const handleRunIndexer = useCallback(async () => {
    const response = await api.fetch(`${MAIN_URL}help/admin/index/run`, true, "POST");
    if (response && response.isSuccessful) {
      enqueueSnackbar(Translate.get("HelpIndexUpdated"), { variant: "success" });
    }
  }, [api, enqueueSnackbar]);

  const existingIds = pages.map((p) => p.helpPageId).filter(Boolean);

  const columns = [
    { field: "helpPageId", headerName: Translate.get("HelpArticleId"), width: 250 },
    { field: "description", headerName: Translate.get("HelpArticleDescriptionInternal"), flex: 1 },
    {
      field: "dotMenu",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "right",
      renderCell: (params) => (
        <DotMenu
          horizontal
          content={[
            {
              icon: <EditIcon />,
              text: Translate.get("Alter"),
              dialogHeader: Translate.get("Alter"),
              customClass: "hugeDialog",
              component: (
                <HelpArticleEditDialog
                  helpPageId={params.row.helpPageId}
                  existingIds={existingIds}
                  categories={categories}
                  onSaved={reloadList}
                />
              ),
            },
            {
              icon: <DeleteIcon />,
              text: Translate.get("Delete"),
              onClick: () => handleDelete(params.row),
            },
          ]}
        />
      ),
    },
  ];

  const speedMenuContent = [
    {
      icon: <AddIcon />,
      text: Translate.get("HelpPagesCreateNew"),
      dialogHeader: Translate.get("HelpPagesCreateNew"),
      component: <HelpArticleEditDialog existingIds={existingIds} categories={categories} onSaved={reloadList} />,
    },
    {
      icon: <RefreshIcon />,
      text: Translate.get("HelpPagesUpdateIndex"),
      onClick: handleRunIndexer,
    },
    {
      icon: <CategoryIcon />,
      text: Translate.get("ManageHelpArticleCategories"),
      dialogHeader: Translate.get("ManageHelpArticleCategories"),
      customClass: "bigCourseDialog",
      component: <HelpCategoriesDialog onSaved={loadCategories} />,
    },
  ];

  return (
    <>
      <SpeedMenu content={speedMenuContent} />
      <Box sx={{ height: "100%", width: "100%" }}>
        {loadingList ? (
          <Preloader />
        ) : (
          <DataGrid
            rows={pages}
            columns={columns}
            getRowId={(row) => row.helpPageId ?? row.name}
            disableRowSelectionOnClick
          />
        )}
      </Box>
    </>
  );
}
